import { css } from '@emotion/react';
import {
  useCallback, useMemo,
} from 'react';
import { useDispatch } from 'react-redux';
import { TriStateCheckboxComponent } from '~/_shared/baseComponents/checkbox';
import {
  PermanentConfirmStrategy, useConfirmationModal,
} from '~/_shared/components/modal/confirmation/useConfirmationModal';
import { TriStateRange } from '~/_shared/constants/triStateRange.enum';
import type { Theme } from '~/_shared/themes/theme.model';
import { MapTool } from '~/_shared/types/toolsAndFeatures/mapTools.types';
import { useIsDataExportAllowed } from '~/_shared/types/toolsAndFeatures/useIsDataExportAllowed';
import { useTranslation } from '~/_shared/utils/hooks';
import { useSelector } from '~/_shared/utils/hooks/useSelector';
import { notNullsy } from '~/_shared/utils/typeGuards';
import {
  BoundaryTerritoryListingExportLimit, BoundaryTerritoryListingLimit,
} from '~/boundary/constants';
import { useLoadMoreListing } from '~/boundary/hooks/useLoadMoreListing';
import { ModalType } from '~/modal/modalType.enum';
import { useModal } from '~/modal/useModal.hook';
import { ActiveBoundaryComponent } from '~/sidebar/sidebarApps/mapTools/boundary/activeBoundary/activeBoundary.component';
import { ActiveBoundaryOptimizedTrialToolbarComponent } from '~/sidebar/sidebarApps/mapTools/boundary/activeBoundary/activeBoundaryOptimizedTrialToolbar.component';
import { ActiveBoundaryToolbarComponent } from '~/sidebar/sidebarApps/mapTools/boundary/activeBoundary/activeBoundaryToolbar.component';
import { BoundaryItemContainer } from '~/sidebar/sidebarApps/mapTools/boundary/activeBoundary/boundaryItem/boundaryItem.container';
import { BoundaryRowLoadMoreComponent } from '~/sidebar/sidebarApps/mapTools/boundary/activeBoundary/boundaryItem/boundaryRowLoadMore.component';
import { BoundaryTerritoryContainer } from '~/sidebar/sidebarApps/mapTools/boundary/activeBoundary/boundaryItem/boundaryTerritory.container';
import { useOptimizedTerritoriesFeatureBlocker } from '~/sidebar/sidebarApps/mapTools/boundary/activeBoundary/boundaryItem/useOptimizedTerritoriesFeatureBlocker';
import { useBoundaryEdit } from '~/sidebar/sidebarApps/mapTools/boundary/edit/useBoundaryEdit';
import { useBoundaryTerritoryGroupDisplayName } from '~/sidebar/sidebarApps/mapTools/boundary/hooks/useBoundaryTerritoryGroupDisplayName';
import type { BoundaryGroupId } from '~/store/boundaries/boundaryIdentifier.type';
import {
  checkBoundaryIsCustom, useBoundaryGroupPermissions,
  useGetBoundaryGroup,
} from '~/store/boundaryGroups/boundaryGroups.selectors';
import { boundaryTerritoryGroupsRemoveRequest } from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.actionCreators';
import {
  boundarySelectEditBoundaryTerritoryOpenModal, boundarySelectEditCloseModal,
} from '~/store/frontendState/mapTools/boundary/boundarySelect/boundarySelect.actionCreators';
import type { BoundarySelectState } from '~/store/frontendState/mapTools/boundary/boundarySelect/boundarySelect.state';
import { convertBoundariesShowNameModal } from '~/store/frontendState/mapTools/boundary/convertBoundaries/convertBoundaries.actionCreators';
import { mapSettingsBoundariesSetPrimaryBoundaryGroupId } from '~/store/mapSettings/boundaries/mapSettingsBoundaries.actionCreators';
import { useMapSettingsBoundariesPrimaryBoundaryGroupIdSelector } from '~/store/mapSettings/boundaries/mapSettingsBoundaries.selectors';
import { setBoundaryFilter } from '~/store/mapSettings/toolsState/boundary/mapSettingsToolsStateBoundary.actionCreators';
import { useBoundaryFilters } from '~/store/mapSettings/toolsState/boundary/mapSettingsToolsStateBoundary.selectors';
import { openModal } from '~/store/modal/modal.actionCreators';
import { useIsMapEditDisabled } from '~/store/selectors/useMapInfoSelectors';
import type { SortedBTGs } from '../boundaryTools.types';
import { useBoundaryItems } from '../hooks/useBoundaryItems';
import { useBTGFillData } from '../hooks/useBTGFillData';
import {
  boundaryOptionsHeaderStyle, boundaryOptionStyle, checkboxStyle,
} from './styles';

type ActiveBoundaryContainerProps = {
  readonly boundaryGroupId: BoundaryGroupId;
  readonly boundaryTerritoryGroups: SortedBTGs;
  readonly legendMode?: boolean;
};

const trialTerritoriesNotLoadedMessageStyle = (theme: Theme) => css({
  fontSize: '14px',
  margin: 0,
  padding: 10,
  color: theme.textColors.secondary,
});

export const ActiveBoundaryContainer = ({
  boundaryGroupId,
  boundaryTerritoryGroups,
  legendMode,
}: ActiveBoundaryContainerProps) => {
  const sidebarBTGHighlightedId = useSelector(state => state.frontendState.mapTools.boundary.boundarySidebarHighlight.highlightedBoundaryTerritoryGroupId);
  const dispatch = useDispatch();
  const { openModal: openBoundaryCustomizeMetricsModal } = useModal(ModalType.BoundaryCustomizeMetrics);
  const { openModal: openBoundaryCustomizeFillsModal } = useModal(ModalType.BoundaryCustomizeFills);
  const { openModal: openCloneBoundaryTerritoryGroupModal } = useModal(ModalType.CloneBoundaryTerritoryGroup);
  const { openModal: openBoundaryAddFillModal } = useModal(ModalType.BoundaryAddFill);
  const boundaryFilters = useBoundaryFilters();
  const primaryBoundaryGroupId = useMapSettingsBoundariesPrimaryBoundaryGroupIdSelector();
  const isEditModeDisabled = useIsMapEditDisabled();
  const isBoundaryExportLocationsAllowed = useIsDataExportAllowed(MapTool.Boundary);
  const { startBoundaryEdit } = useBoundaryEdit();
  const { getBoundaryTerritoryGroupName } = useBoundaryTerritoryGroupDisplayName();
  const { openModal: openExportBoundaryTerritoryGroupDataModal } = useModal(ModalType.ExportBoundaryTerritoryGroupData);
  const { openConfirmationModal, closeConfirmationModal } = useConfirmationModal();
  const { openModal: openBoundaryTerritoryEditSubmitModal } = useModal(ModalType.BoundaryTerritoryEditSubmit);
  const { openModal: openUpgradeSubscriptionModal } = useModal(ModalType.WMSUpgradeSubscription);
  const [t] = useTranslation();

  const {
    allowFillChange,
    activeBoundaryTerritoryGroup,
    activeBTGOption,
    filteredBTGDropDownOptions,
    setActiveBTGId,
    isOnlyNofillOption,
  } = useBTGFillData({
    boundaryGroupId,
    boundaryTerritoryGroups,
  });

  const {
    boundaryTerritoryList: boundaryTerritoryArray,
    displayBoundaryTerritories,
    areGroupBoundariesEmpty,
    sortedBoundaryList: sortedBoundaryArray,
  } = useBoundaryItems({ boundaryTerritoryGroup: activeBoundaryTerritoryGroup });

  const {
    boundaryIsOptimizedTerritoriesTrial,
    blockWhenOptimizedTerritoriesTrial,
    remainingGeometryCalls,
  } = useOptimizedTerritoriesFeatureBlocker(activeBoundaryTerritoryGroup.boundaryGroupId);

  const initialRowLimit = legendMode ? BoundaryTerritoryListingExportLimit : BoundaryTerritoryListingLimit;
  const {
    limit: shownBoundaryKeySettingCountLimit,
    increaseLimit: increaseShownBoundaryKeySettingCountLimit,
  } = useLoadMoreListing(initialRowLimit);
  const {
    limit: shownBoundaryTerritoryKeySettingCountLimit,
    increaseLimit: increaseShownBoundaryTerritoryKeySettingCountLimit,
  } = useLoadMoreListing(initialRowLimit);

  const displayName = getBoundaryTerritoryGroupName(activeBoundaryTerritoryGroup);
  const boundaryPermissions = useBoundaryGroupPermissions(boundaryGroupId);
  const { boundaryIsCustom } = boundaryPermissions;
  const { getBoundaryGroupById } = useGetBoundaryGroup();

  const filter = boundaryFilters.get(boundaryGroupId);

  const removeBoundaryFromMap = useCallback(() => {
    const boundaryGroup = getBoundaryGroupById(boundaryGroupId);
    const isCustom = checkBoundaryIsCustom(boundaryGroup);
    const entity = isCustom ? t('territory group') : t('boundary');

    openConfirmationModal({
      title: t('Remove {{entity}} from map', { entity }),
      confirmCaption: t('Proceed'),
      text: t('You are about to remove a {{entity}} from the map. Do you want to proceed?',
        { entity }),
      permanentConfirmSettings: {
        id: 'remove-boundary-territory-group-from-map',
        strategy: PermanentConfirmStrategy.Session,
      },
      isConfirmButtonDestructive: true,
      onCancel: () => {
        closeConfirmationModal();
      },
      onConfirm: () => {
        dispatch(boundaryTerritoryGroupsRemoveRequest(boundaryTerritoryGroups.filter(notNullsy).map(btg => btg.boundaryTerritoryGroupId)));
        closeConfirmationModal();
      },
    });
  }, [boundaryGroupId, boundaryTerritoryGroups, closeConfirmationModal, dispatch, getBoundaryGroupById, openConfirmationModal, t]);

  const convertTerritoriesToBoundaries = useCallback((boundaryTerritoryGroupId: number) => {
    dispatch(convertBoundariesShowNameModal(boundaryTerritoryGroupId));
  }, [dispatch]);

  const onToggleShowAll = useCallback(() => {
    dispatch(setBoundaryFilter(activeBoundaryTerritoryGroup.boundaryGroupId, {
      ...filter,
      filteredBoundaryTerritories: [],
      filteredBoundaries: [],
      showAll: filter.showAll === TriStateRange.Full
        ? TriStateRange.None
        : TriStateRange.Full,
    }));
  }, [activeBoundaryTerritoryGroup.boundaryGroupId, dispatch, filter]);

  const onClone = useCallback(() => openCloneBoundaryTerritoryGroupModal({
    boundaryTerritoryGroupId: activeBoundaryTerritoryGroup.boundaryTerritoryGroupId,
  }), [openCloneBoundaryTerritoryGroupModal, activeBoundaryTerritoryGroup.boundaryTerritoryGroupId]);

  const handleSettingsClick = useCallback(() => {
    dispatch(openModal(ModalType.BoundarySettings, {
      boundaryTerritoryGroupId: activeBoundaryTerritoryGroup.boundaryTerritoryGroupId,
    }));
  }, [dispatch, activeBoundaryTerritoryGroup.boundaryTerritoryGroupId]);

  const handleAddFillClick = useMemo(() => blockWhenOptimizedTerritoriesTrial(() => {
    openBoundaryAddFillModal({
      boundaryGroupId: activeBoundaryTerritoryGroup.boundaryGroupId,
    });
  }), [activeBoundaryTerritoryGroup.boundaryGroupId, blockWhenOptimizedTerritoriesTrial, openBoundaryAddFillModal]);

  const handleCustomizeFillClick = useMemo(() => blockWhenOptimizedTerritoriesTrial(() => {
    openBoundaryCustomizeFillsModal({
      boundaryGroupId: activeBoundaryTerritoryGroup.boundaryGroupId,
    });
  }),
  [blockWhenOptimizedTerritoriesTrial, openBoundaryCustomizeFillsModal, activeBoundaryTerritoryGroup.boundaryGroupId]);

  const handleCustomizeMetricsClick = useCallback(() => {
    openBoundaryCustomizeMetricsModal({
      boundaryTerritoryGroupId: activeBoundaryTerritoryGroup.boundaryTerritoryGroupId,
      isBoundaryGroupCustom: boundaryIsCustom,
    });
  }, [openBoundaryCustomizeMetricsModal, activeBoundaryTerritoryGroup.boundaryTerritoryGroupId, boundaryIsCustom]);

  const handleStartEditClick = useMemo(() => {
    if (boundaryIsCustom && !boundaryPermissions.boundaryIsEditable) {
      return undefined;
    }

    return blockWhenOptimizedTerritoriesTrial(() => {
      if (boundaryIsCustom) {
        startBoundaryEdit(activeBoundaryTerritoryGroup.boundaryTerritoryGroupId);
      }
      else {
        dispatch(boundarySelectEditBoundaryTerritoryOpenModal({
          selectBoundaryGroupId: activeBoundaryTerritoryGroup.boundaryGroupId,
          boundaryTerritoryGroupId: activeBoundaryTerritoryGroup.boundaryTerritoryGroupId,
          onClose: () => {
            dispatch(boundarySelectEditCloseModal());
          },
          onSubmit: (selectState: BoundarySelectState) => {
            if (!selectState.selectedBoundaryIds?.size) {
              return;
            }

            openBoundaryTerritoryEditSubmitModal({
              boundaryTerritoryGroupId: activeBoundaryTerritoryGroup.boundaryTerritoryGroupId,
              boundaryIds: Array.from(selectState.selectedBoundaryIds.keys()),
            });
          },
        }));
      }
    });
  },
  [boundaryIsCustom, boundaryPermissions.boundaryIsEditable, blockWhenOptimizedTerritoriesTrial, startBoundaryEdit,
    activeBoundaryTerritoryGroup.boundaryTerritoryGroupId, activeBoundaryTerritoryGroup.boundaryGroupId, dispatch,
    openBoundaryTerritoryEditSubmitModal]);

  const handleToggleIsPrimary = useCallback(() => {
    dispatch(mapSettingsBoundariesSetPrimaryBoundaryGroupId(activeBoundaryTerritoryGroup.boundaryGroupId));
  }, [dispatch, activeBoundaryTerritoryGroup.boundaryGroupId]);

  const handleConvert = useCallback(() =>
    convertTerritoriesToBoundaries(activeBoundaryTerritoryGroup.boundaryTerritoryGroupId),
  [convertTerritoriesToBoundaries, activeBoundaryTerritoryGroup.boundaryTerritoryGroupId],
  );

  const handleExportClick = useCallback(() =>
    openExportBoundaryTerritoryGroupDataModal({
      selectedBoundaryTerritoryGroupIds: [activeBoundaryTerritoryGroup.boundaryTerritoryGroupId],
      showSelectBoundaryTerritoryGroupsSection: false,
    }),
  [openExportBoundaryTerritoryGroupDataModal, activeBoundaryTerritoryGroup.boundaryTerritoryGroupId],
  );

  const containsLegend = sortedBoundaryArray.length || boundaryTerritoryArray.length;

  if (legendMode && !containsLegend) {
    return null;
  }

  const isBtgTrialAndEmpty = boundaryIsOptimizedTerritoriesTrial && remainingGeometryCalls === 0 && areGroupBoundariesEmpty;

  const renderBuckets = () => {
    return (
      <>
        {!legendMode && !isBtgTrialAndEmpty && (
          <div css={boundaryOptionsHeaderStyle}>
            <div css={boundaryOptionStyle}>
              <TriStateCheckboxComponent
                checkmark={filter.showAll}
                checkedSetter={onToggleShowAll}
                text={boundaryIsCustom ? t('Show All Territories') : t('Show All Boundaries')}
                textStyle={checkboxStyle}
              />
            </div>
          </div>
        )}

        {displayBoundaryTerritories && (
          <>
            {boundaryTerritoryArray.map(
              (boundaryTerritory, index) => {
                return (index < shownBoundaryTerritoryKeySettingCountLimit) && (
                  <BoundaryTerritoryContainer
                    key={boundaryTerritory.boundaryTerritoryId}
                    boundaryTerritory={boundaryTerritory}
                    boundaryTerritoryGroup={activeBoundaryTerritoryGroup}
                    legendMode={legendMode}
                  />
                );
              },
            )}

            {(boundaryTerritoryArray.length > shownBoundaryTerritoryKeySettingCountLimit) && (
              <BoundaryRowLoadMoreComponent
                onButtonClick={increaseShownBoundaryTerritoryKeySettingCountLimit}
              />
            )}
          </>
        )}
      </>
    );
  };

  const toolbar = isBtgTrialAndEmpty ? (
    <ActiveBoundaryOptimizedTrialToolbarComponent
      boundaryPermissions={boundaryPermissions}
      onRemove={removeBoundaryFromMap}
      onPurchaseClick={openUpgradeSubscriptionModal}
    />
  ) : (
    <ActiveBoundaryToolbarComponent
      boundaryIsOptimizedTerritoriesTrial={boundaryIsOptimizedTerritoriesTrial}
      boundaryPermissions={boundaryPermissions}
      boundaryGroupId={activeBoundaryTerritoryGroup.boundaryGroupId}
      onSettingsClick={handleSettingsClick}
      onRemove={removeBoundaryFromMap}
      onAddFillClick={handleAddFillClick}
      onCustomizeFillClick={handleCustomizeFillClick}
      onCustomizeMetricsClick={handleCustomizeMetricsClick}
      onStartEditClick={handleStartEditClick}
      onConvert={activeBoundaryTerritoryGroup.settings.boundaryTerritories.length ? handleConvert : undefined}
      onClone={!isEditModeDisabled && boundaryIsCustom ? onClone : undefined}
      onExportClick={isBoundaryExportLocationsAllowed ? handleExportClick : undefined}
    />
  );

  return (
    <ActiveBoundaryComponent
      dropdownProps={{
        allowFillChange,
        activeBTGOption,
        filteredBTGDropDownOptions,
        setActiveBTGId,
        isOnlyNofillOption,
      }}
      isHighlighted={activeBoundaryTerritoryGroup.boundaryGroupId === sidebarBTGHighlightedId}
      showActions={!legendMode}
      name={displayName}
      toolbar={isEditModeDisabled ? null : toolbar}
      boundaryIsOptimizedTerritoriesTrial={boundaryIsOptimizedTerritoriesTrial}
      isPrimary={activeBoundaryTerritoryGroup.boundaryGroupId === primaryBoundaryGroupId}
      onToggleIsPrimary={handleToggleIsPrimary}
    >

      {renderBuckets()}

      {(!displayBoundaryTerritories && !areGroupBoundariesEmpty) && sortedBoundaryArray
        .map((boundary, index) => {
          if (index < shownBoundaryKeySettingCountLimit) {
            return (
              <BoundaryItemContainer
                boundary={boundary}
                boundaryPermissions={boundaryPermissions}
                boundaryTerritoryGroup={activeBoundaryTerritoryGroup}
                key={boundary.id}
                legendMode={legendMode}
              />
            );
          }

          return null;
        })}

      {isBtgTrialAndEmpty && (
        <p css={trialTerritoriesNotLoadedMessageStyle}>
          {t('Territories are hidden because you\'re currently using ATB in trial mode. To view them, please upgrade to a full plan.')}
        </p>
      )}

      {!displayBoundaryTerritories && (sortedBoundaryArray.length > shownBoundaryKeySettingCountLimit) && (
        <BoundaryRowLoadMoreComponent
          onButtonClick={increaseShownBoundaryKeySettingCountLimit}
        />
      )}

    </ActiveBoundaryComponent>
  );
};
